import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  Support as SupportIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import DocumentMarketingHero from '../components/DocumentMarketingHero';
import { documentPageCanvasSx } from '../constants/documentTheme';
import { partnerOxbloodBtnSx } from '../components/PartnerListChrome';
import { useTranslation } from 'react-i18next';

const Support = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const troubleshootingItems = [
    {
      id: 'login-issues',
      title: 'Can\'t log in to my account',
      icon: <WarningIcon color="warning" />,
      content: (
        <Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            If you're having trouble logging in, try these steps:
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Make sure you're using the correct email address
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Check your email for verification link (check spam folder)
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Try the "Forgot Password" option to reset your password
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Clear your browser cache and cookies
            </Typography>
          </Stack>
        </Box>
      ),
    },
    {
      id: 'app-loading',
      title: 'App is not loading or showing errors',
      icon: <WarningIcon color="warning" />,
      content: (
        <Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            If the app isn't loading properly:
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Refresh the page (Ctrl+F5 or Cmd+Shift+R)
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Clear your browser cache and cookies
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Try a different browser (Chrome, Firefox, Safari)
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Check your internet connection
            </Typography>
          </Stack>
        </Box>
      ),
    },
    {
      id: 'invitations',
      title: 'Trip invitations not working',
      icon: <WarningIcon color="warning" />,
      content: (
        <Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            If you're not receiving trip invitations:
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Check your email spam/junk folder
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Ask the trip creator to resend the invitation
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Make sure you're using the same email address for your account
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Try logging in and checking your trips list
            </Typography>
          </Stack>
        </Box>
      ),
    },
    {
      id: 'mobile-app',
      title: 'Mobile app installation issues',
      icon: <WarningIcon color="warning" />,
      content: (
        <Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            For mobile app installation:
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              iOS: Use Safari browser and tap "Add to Home Screen"
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Android: Use Chrome and tap "Add to Home Screen"
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Make sure you're on the main page when installing
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Allow notifications when prompted for better experience
            </Typography>
          </Stack>
        </Box>
      ),
    },
    {
      id: 'trip-creation',
      title: 'Can\'t create or edit trips',
      icon: <WarningIcon color="warning" />,
      content: (
        <Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            If you're having trouble with trip creation:
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Make sure you're logged in to your account
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Check that all required fields are filled out
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Ensure trip end date is after start date
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Try refreshing the page and starting over
            </Typography>
          </Stack>
        </Box>
      ),
    },
    {
      id: 'delete-account',
      title: 'How do I delete my account?',
      icon: <WarningIcon color="error" />,
      content: (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Account deletion is permanent and cannot be undone.
            </Typography>
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            To delete your account:
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Go to your Profile page (click your avatar)
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Scroll to the bottom to find the "Danger Zone" section
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Click "Delete My Account" button
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" sx={{ fontSize: 16 }} />
              Type your username to confirm deletion
            </Typography>
          </Stack>
        </Box>
      ),
    },
  ];

  const contactMethods = [
    {
      title: 'Email Support',
      description: 'Get help via email',
      icon: <EmailIcon sx={{ fontSize: 40, color: '#591716' }} />,
      action: () => window.open('mailto:support@dejavunomad.com', '_blank'),
      buttonText: 'Send Email',
    },
    {
      title: 'Back to Home',
      description: 'Return to the main page',
      icon: <SupportIcon sx={{ fontSize: 40, color: '#591716' }} />,
      action: () => navigate('/'),
      buttonText: 'Go Home',
    },
  ];

  // FAQ Schema for SEO - extracted from troubleshooting items
  const faqSchemaItems = [
    {
      question: "Can't log in to my Déjà Vu Nomad account",
      answer: "If you're having trouble logging in: 1) Make sure you're using the correct email address, 2) Check your email for verification link (check spam folder), 3) Try the 'Forgot Password' option to reset your password, 4) Clear your browser cache and cookies."
    },
    {
      question: "Déjà Vu Nomad app is not loading or showing errors",
      answer: "If the app isn't loading properly: 1) Refresh the page (Ctrl+F5 or Cmd+Shift+R), 2) Clear your browser cache and cookies, 3) Try a different browser (Chrome, Firefox, Safari), 4) Check your internet connection."
    },
    {
      question: "Trip invitations not working in Déjà Vu Nomad",
      answer: "If you're not receiving trip invitations: 1) Check your email spam/junk folder, 2) Ask the trip creator to resend the invitation, 3) Make sure you're using the same email address for your account, 4) Try logging in and checking your trips list."
    },
    {
      question: "How to install Déjà Vu Nomad mobile app",
      answer: "For mobile app installation: iOS - Use Safari browser and tap 'Add to Home Screen'. Android - Use Chrome and tap 'Add to Home Screen'. Make sure you're on the main page when installing. Allow notifications when prompted for better experience."
    },
    {
      question: "How do I delete my Déjà Vu Nomad account?",
      answer: "To delete your account: 1) Go to your Profile page (click your avatar), 2) Scroll to the bottom to find the 'Danger Zone' section, 3) Click 'Delete My Account' button, 4) Type your username to confirm deletion. Note: Account deletion is permanent and cannot be undone."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqSchemaItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <Box sx={documentPageCanvasSx}>
      <SEOHead
        title="Support Center | Help & Troubleshooting - Déjà Vu Nomad"
        description="Get help with Déjà Vu Nomad vacation planning app. Find answers to common questions about login issues, trip invitations, mobile app installation, and more. Contact our support team for assistance."
        keywords="Déjà Vu Nomad support, trip planner help, vacation app troubleshooting, travel app support, login issues, trip invitations, mobile app help"
        canonical="https://dejavunomad.com/support"
        structuredData={faqSchema}
      />
      <DocumentMarketingHero
        eyebrow={t('home.footer.legal')}
        title="Support Center"
        subtitle="Need help with Déjà Vu Nomad? Find answers to common questions and get in touch with our support team."
      />
    <Container maxWidth="xl" sx={{ py: 4 }}>

      {/* Quick Contact Cards */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        {contactMethods.map((method, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Card 
              sx={{ 
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 8px 24px rgba(89, 23, 22, 0.15)',
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <Box sx={{ mb: 2 }}>
                  {method.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {method.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#313848', mb: 3 }}>
                  {method.description}
                </Typography>
                <Button
                  variant="contained"
                  onClick={method.action}
                  sx={partnerOxbloodBtnSx}
                >
                  {method.buttonText}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Troubleshooting Section */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
          Troubleshooting Guide
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'center', mb: 4, color: '#313848' }}>
          Find solutions to common issues below. Click on any topic to expand and see detailed steps.
        </Typography>
        
        {troubleshootingItems.map((item) => (
          <Accordion
            key={item.id}
            expanded={expanded === item.id}
            onChange={handleChange(item.id)}
            sx={{
              mb: 2,
              borderRadius: 2,
              '&:before': {
                display: 'none',
              },
              boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: expanded === item.id ? 'rgba(89, 23, 22, 0.05)' : 'transparent',
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {item.icon}
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {item.title}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: 'rgba(89, 23, 22, 0.02)' }}>
              {item.content}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Additional Help Section */}
      <Card sx={{ backgroundColor: 'rgba(89, 23, 22, 0.05)', borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            <InfoIcon sx={{ fontSize: 48, color: '#591716', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Still Need Help?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: '#313848' }}>
              If you couldn't find the answer you're looking for, our support team is ready to help you.
            </Typography>
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>Response Time:</strong> We typically respond within 24 hours during business days.
              </Typography>
            </Alert>
            <Button
              variant="contained"
              size="large"
              startIcon={<EmailIcon />}
              onClick={() => window.open('mailto:support@dejavunomad.com', '_blank')}
              sx={{
                ...partnerOxbloodBtnSx,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
              }}
            >
              Contact Support Team
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
    </Box>
  );
};

export default Support; 